import axios, { AxiosResponse, CancelToken } from 'axios';

import { CommonModel } from '@telialt/models/src/CommonModel';
import { CompanyInfoRequestBody, VatConfigRequestBody } from '@telialt/models/src/EshopCheckoutB2B';
import {
    AccountSubscriptionOption,
    AddProductBody,
    Bank,
    BanklinkPaymentResponse,
    BaseProduct,
    CardType,
    Cart,
    CartCodeParam,
    CartId,
    CartIdParam,
    CartModification,
    CombinedAddresses,
    ConsentTemplate,
    ContractType,
    CreditCheckResponse,
    DeliveryModesResponse,
    DisneyPlan,
    MigrateCartParams,
    NetflixPlan,
    OrderDocument,
    PlaceOrderParam,
    ProductReferences,
    ProductVariant,
    RegisterCartStepParams,
    RemoveProductParams,
    RentingPeriod,
    SavePlanOrderBody,
    SetAddressesBodyB2B,
    SetDeliveryModeParams,
    TelcoOperator,
    TermsOfUseTextResponse,
} from '@telialt/models/src/EshopModel';
import { Account } from '@telialt/models/src/SelfService';
import { responseErrorInterceptor } from '@telialt/utils/src/authentication/responseErrorInterceptor';
import { getAuthToken } from '@telialt/utils/src/authentication/utils';
import { axiosForHybris } from '@telialt/utils/src/axios/axiosForHybris';
import { datadogLogger } from '@telialt/utils/src/datadog/datadogLogger';
import { DDComponent } from '@telialt/utils/src/datadog/setup';
import { checkShouldUseMocks } from '@telialt/utils/src/envUtils';
import { isSSR, withParams } from '@telialt/utils/src/helpers';
import { getSiteName, TeliaSegment } from '@telialt/utils/src/segment';
import { getUserId } from '@telialt/utils/src/SSO/utils';

import { baseProductFilteringParams } from '../constants';
import { CartMocksKeys } from '../test/mocks/mocks';
import { BaseProductMocksKeys } from '../test/mocks/productMocks';
import { API } from './constants';

export const axiosWithAuth = axios.create({
    baseURL: checkShouldUseMocks() ? process.env.HYBRIS_PUBLIC_URL : '',
});

if (!isSSR()) {
    axiosWithAuth.interceptors.request.use((config) => {
        return getAuthToken().then(({ accessToken, tokenType }) => {
            config.headers.Authorization = `${tokenType} ${accessToken}`;

            if (!accessToken) {
                const datadogLogs = datadogLogger();
                datadogLogs.warn('Request with undefined eshop token', {
                    config,
                    component: DDComponent.AUTH,
                });
            }

            return config;
        });
    });
    axiosWithAuth.interceptors.response.use(
        (response) => response,
        responseErrorInterceptor(axiosWithAuth, getAuthToken),
    );
}

/** @name Params.mock
 * pass query parameter to return mock object, from existing stubs
 * the requests are managed by an express middleware,
 * which should be chained to existing development server
 * @see cartMocks
 * @see baseProductMocks
 * @see mockApiApp.ts
 * */
export type Params = { fields?: string; mock?: BaseProductMocksKeys | CartMocksKeys } & Record<any, any>;

const defaultParams: Params = { fields: 'FULL' };

export const apiGetProduct = async <Product extends BaseProduct | ProductVariant>(
    segment: TeliaSegment,
    productCode: string,
    params: Params = defaultParams,
    signal?: AbortSignal,
) => {
    const siteName = getSiteName(segment);
    const url: string = withParams(API.URL_PRODUCT, { siteName, productCode });

    return axiosForHybris.get<Product>(url, { params, signal });
};

export const apiGetBaseProductFromUrlParam = async (segment: TeliaSegment, urlParam: string, params?: Params) =>
    apiGetProduct<BaseProduct>(segment, urlParam, { ...params, byPath: true } as Params);

export const getProductReferences = async (
    segment: TeliaSegment,
    productCode: string,
): Promise<AxiosResponse<ProductReferences>> => {
    const siteName = getSiteName(segment);
    const url: string = withParams(API.URL_PRODUCT_REFERENCES, { siteName, productCode });

    return axiosForHybris.get<ProductReferences>(url);
};

export const apiGetCart = async (
    segment: TeliaSegment,
    { cartId, ...params }: CartIdParam & Params,
    cancelToken?: CancelToken,
    signal?: AbortSignal,
) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_GET_CART, { siteName, cartId });

    return axiosWithAuth.get<Cart>(url, { cancelToken, signal, params: { ...defaultParams, ...params } as Params });
};

export const apiPingCart = (segment: TeliaSegment, cartId: CartId) =>
    apiGetCart(segment, { cartId, fields: 'code,guid' });

export const apiPingOrder = async (segment: TeliaSegment, cartId: CartId) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_GET_ORDER, { siteName, cartId });

    return axiosWithAuth.put<Cart>(url, null, { params: { fields: 'code,guid' } });
};

export const apiCreateCart = async (segment: TeliaSegment) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(withParams(API.URL_CREATE_CART, { siteName }));

    return axiosWithAuth.post<Cart>(url);
};

export const apiMigrateCart = async (segment: TeliaSegment, params: MigrateCartParams) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(withParams(API.URL_MIGRATE_CART, { siteName }));

    return axiosWithAuth.post<Cart>(url, null, { params: { ...defaultParams, ...params } });
};

/** cart cloning is done only by "cart.guid", shouldn't consider "cart.code"
 * > cart is cloned only when we get a special offer from url & the cart has "generated == true"
 *
 * @see Cart.code
 * @see Cart.guid
 * @see getCartIdSync
 * @see Cart.generated
 * */
export const apiCloneCart = async (segment: TeliaSegment, params: CartIdParam) => {
    const siteName = getSiteName(segment);
    const url = await withUrlParamsAndUserId(API.URL_CLONE_CART, { siteName, ...params });

    return axiosWithAuth.post<Cart>(url);
};

export const apiAddProduct = async (segment: TeliaSegment, params: CartIdParam, body: AddProductBody) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_ADD_CART_ENTRY, { siteName, ...params });

    return axiosWithAuth.post<CartModification>(url, body, { params: defaultParams });
};

export const apiAddRatePlan = async (segment: TeliaSegment, params: CartIdParam, ratePlanCode: string) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_ADD_RATE_PLAN, {
        siteName,
        cartId: params.cartId,
        ratePlanCode,
    });

    return axiosWithAuth.post<CartModification>(url, null, { params: defaultParams });
};

export const apiDeleteRatePlan = async (segment: TeliaSegment, params: CartIdParam) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_RATE_PLAN, { siteName, cartId: params.cartId });

    return axiosWithAuth.delete<CartModification>(url, { params: defaultParams });
};

export const apiAddLeasingPlan = async (segment: TeliaSegment, params: CartIdParam, leasingPlanCode: string) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_ADD_LEASING_PLAN, {
        siteName,
        cartId: params.cartId,
        leasingPlanCode,
    });

    return axiosWithAuth.post<Cart>(url, null, { params: defaultParams });
};

export const apiDeleteLeasingPlan = async (segment: TeliaSegment, params: CartIdParam) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_LEASING_PLAN, {
        siteName,
        cartId: params.cartId,
    });

    return axiosWithAuth.delete<Cart>(url, { params: defaultParams });
};

export const apiRemoveProduct = async (segment: TeliaSegment, params: RemoveProductParams) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_REMOVE_CART_ENTRY, { siteName, ...params });

    return axiosWithAuth.delete<Cart>(url);
};

export const apiUpdateCartInsurance = async (
    segment: TeliaSegment,
    params: CartIdParam,
    insuranceSelected: boolean,
) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_UPDATE_CART_INSURANCE, { siteName, ...params });

    return axiosWithAuth.post<Cart>(url, { insuranceSelected });
};

export const apiUpdateCartNetflix = async (segment: TeliaSegment, params: CartIdParam, netflixPlan: NetflixPlan) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_UPDATE_CART_NETFLIX, { siteName, ...params });

    return axiosWithAuth.put<Cart>(url, { netflixPlan }, { params: defaultParams });
};

export const apiUpdateCartDisney = async (segment: TeliaSegment, params: CartIdParam, disneyPlan: DisneyPlan) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_UPDATE_CART_DISNEY, { siteName, ...params });

    return axiosWithAuth.put<Cart>(url, { disneyPlan }, { params: defaultParams });
};

export const apiUpdateInitialPaymentAmount = async (
    segment: TeliaSegment,
    { cartId, ...params }: CartIdParam & Params,
    selectedInitialPayment: number,
) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_UPDATE_INITIAL_PAYMENT_AMOUNT, { siteName, cartId });

    return axiosWithAuth.post<Cart>(
        url,
        { selectedInitialPayment: String(selectedInitialPayment) },
        { params: { ...defaultParams, ...params } },
    );
};

export const apiAddGifts = async (segment: TeliaSegment, params: CartIdParam, giftCodes: string[]) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_ADD_GIFTS, { siteName, ...params });

    return axiosWithAuth.post<Cart>(url, {
        selectedGiftProductCodes: giftCodes,
    });
};

export const apiSubmitCoupon = async (
    segment: TeliaSegment,
    { cartId, ...params }: CartIdParam & Params,
    couponCode: string,
) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_COUPONS, { siteName, cartId });

    return axiosWithAuth.post<Cart>(url, { code: couponCode }, { params: { ...defaultParams, ...params } });
};

export const apiDeleteCoupon = async (segment: TeliaSegment, { cartId, ...params }: CartIdParam & Params) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_COUPONS, { siteName, cartId });

    return axiosWithAuth.delete<Cart>(url, { params: { ...defaultParams, ...params } });
};
export const getBanks = (segment: TeliaSegment): Promise<AxiosResponse<Bank[]>> => {
    const siteName = getSiteName(segment);

    return axios.get<Bank[]>(withParams(API.URL_BANKS, { siteName }));
};

export const apiPlaceOrder = async (segment: TeliaSegment, { consentTemplates, ...params }: PlaceOrderParam) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_PLACE_ORDER, { siteName });

    const body = { teliaConsentTemplates: consentTemplates || [] };

    return axiosWithAuth.post<Cart>(url, body, { params });
};

export const apiCancelOrder = async (segment: TeliaSegment, orderId: CartId) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_CANCEL_ORDER, { siteName, orderId });

    return axiosWithAuth.post<Cart>(url, null, { params: defaultParams });
};

export const initiateBanklinkPayment = async (
    segment: TeliaSegment,
    params: CartIdParam,
    bankId: string,
    teliaConsentTemplates?: ConsentTemplate[],
): Promise<AxiosResponse<BanklinkPaymentResponse>> => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_INITIATE_BANKLINK_PAYMENT, { siteName, ...params });

    return axiosWithAuth.put<BanklinkPaymentResponse>(url, { bankId, teliaConsentTemplates });
};

export const initiateCartPopPayment = async (
    segment: TeliaSegment,
    params: CartIdParam,
    bankId: string,
    teliaConsentTemplates?: ConsentTemplate[],
): Promise<AxiosResponse<BanklinkPaymentResponse>> => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_INITIATE_POP_PAYMENT, { siteName, ...params });

    return axiosWithAuth.put(url, { bankId, teliaConsentTemplates });
};

export const apiRegisterCartStep = async (
    segment: TeliaSegment,
    { cartId, cartStepEnum, contractType }: RegisterCartStepParams,
) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_REGISTER_CART_STEP, { siteName, cartId });

    return axiosWithAuth.post<void, void>(url, undefined, { params: { cartStepEnum, contractType } });
};

export const apiRegisterAbstractOrderStep = async (
    segment: TeliaSegment,
    { cartId, cartStepEnum, contractType }: RegisterCartStepParams,
) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_REGISTER_ABSTRACT_ORDER_STEP, { siteName, cartId });

    return axiosWithAuth.post<void, void>(url, undefined, { params: { cartStepEnum, contractType } });
};

export const apiCreditCheck = async (segment: TeliaSegment, params: CartCodeParam) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_CHECKOUT_CREDIT_CHECK, { siteName, ...params });

    return axiosWithAuth.post<CreditCheckResponse>(url);
};

export const apiSavePlanOrder = async (
    segment: TeliaSegment,
    { cartCode, ...params }: CartCodeParam,
    body: SavePlanOrderBody,
) => {
    const siteName = getSiteName(segment);
    const url = await withUrlParamsAndUserId(API.URL_SET_SELECTED_PLAN_ORDER, { siteName, cartCode });

    return axiosWithAuth.put<Cart>(url, body, { params: { ...defaultParams, params } });
};
export const apiGetTelcoOperators = (segment: TeliaSegment) => {
    const siteName = getSiteName(segment);

    return axios.get<TelcoOperator[]>(withParams(API.URL_GET_TELCO_OPERATORS, { siteName }));
};

export const getDeliveryModes = (
    segment: TeliaSegment,
    checkoutType: 'MOB' | 'FIX',
): Promise<AxiosResponse<DeliveryModesResponse>> => {
    const siteName = getSiteName(segment);

    return axiosWithAuth.get<DeliveryModesResponse>(withParams(API.URL_GET_DELIVERY_MODES, { siteName }), {
        params: { checkoutType },
    });
};

export const apiGetConsentTemplate = async (segment: TeliaSegment, consentTemplateType: string) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_GET_CONSENT_TEMPLATE, { siteName, consentTemplateType });

    return axiosWithAuth.get<ConsentTemplate>(url);
};

export const apiSetAddresses = async (
    segment: TeliaSegment,
    params: CartIdParam,
    body: CombinedAddresses | SetAddressesBodyB2B,
): Promise<AxiosResponse<void>> => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_SET_ADDRESSES, { siteName, ...params });

    return axiosWithAuth.post<void>(url, body);
};

export const apiSetDeliveryMode = async (
    segment: TeliaSegment,
    params: SetDeliveryModeParams,
): Promise<AxiosResponse<void>> => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_SET_DELIVERY_MODE, { siteName, ...params });

    return axiosWithAuth.put<void>(url, null, { params: { deliveryModeId: params.deliveryModeId } });
};

export const apiGetOrder = async (segment: TeliaSegment, { cartId, ...params }: CartIdParam & Params) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_GET_ORDER, { siteName, cartId });

    return axiosWithAuth.get<Cart>(url, { params: { ...defaultParams, ...params } });
};

export const apiPostConfirmationPlaceOrder = async (
    segment: TeliaSegment,
    { cartId, ...params }: CartIdParam & Params,
) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_POST_CONFIRMATION_PLACE_ORDER, { siteName, cartId });

    return axiosWithAuth.post<Cart>(url, { params: { ...defaultParams, ...params } });
};

export const apiGetOrderDocument = async (segment: TeliaSegment, cartId: string, documentId: string) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_GET_ORDER_DOCUMENT, { siteName, cartId, documentId });

    return axiosWithAuth.get<OrderDocument>(url);
};

export const apiInitializeSodraDocument = async (segment: TeliaSegment, cartId: string, params = defaultParams) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_GET_SODRA_DOCUMENT, { siteName, cartId });

    return axiosWithAuth.post<Cart>(url, undefined, { params });
};

export const apiGetSodraCreditCheckStatus = async (segment: TeliaSegment, cartId: string) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_GET_SODRA_CREDIT_CHECK_STATUS, { siteName, cartId });

    return axiosWithAuth.get<Cart>(url, { params: { fields: 'status,creditCheckResult' } });
};

export const apiGetTermsOfUseText = async (segment: TeliaSegment, code: string) => {
    const siteName = getSiteName(segment);

    return axios.get<TermsOfUseTextResponse>(withParams(API.URL_GET_TERMS_OF_USE_TEXT, { siteName, code }));
};

export const apiGetEligibleToApplyDiscountCoupon = async (
    segment: TeliaSegment,
    params: { cartId: CartId; couponCode: string },
    signal?: AbortSignal,
) => {
    const siteName = getSiteName(segment);
    const url: string = await withUrlParamsAndUserId(API.URL_GET_IS_ELIGIBLE_TO_APPLY_DISCOUNT, {
        siteName,
        ...params,
    });

    return axiosWithAuth.get<{ discountExceedsPrice: boolean }>(url, { signal });
};

export const apiUpdateContractType = async (segment: TeliaSegment, cartId: string, contractType: ContractType) => {
    const siteName = getSiteName(segment);
    const url = await withUrlParamsAndUserId(API.URL_UPDATE_CONTRACT_TYPE, { siteName, cartId });

    return axiosWithAuth.put<Cart>(url, { contractType });
};

export const apiUpdateRentingPlan = async (segment: TeliaSegment, cartId: string, rentingPeriod: RentingPeriod) => {
    const siteName = getSiteName(segment);
    const url = await withUrlParamsAndUserId(API.URL_UPDATE_RENTING_PLAN, { siteName, cartId, rentingPeriod });

    return axiosWithAuth.post<Cart>(url);
};

export const apiSetVatConfig = async (segment: TeliaSegment, params: CartIdParam, body?: VatConfigRequestBody) => {
    const siteName = getSiteName(segment);
    if (body?.budgetaryInstitution != null) {
        const url = await withUrlParamsAndUserId(API.URL_SET_VAT_CONFIG_BUDGETARY_INSTITUTION, { siteName, ...params });

        return axiosWithAuth.put<Cart>(url, body, { params: defaultParams });
    }

    if (body?.registeredVatPayer != null) {
        const url = await withUrlParamsAndUserId(API.URL_SET_VAT_CONFIG_REGISTERED_VAT_PAYER, { siteName, ...params });

        return axiosWithAuth.put<Cart>(url, body, { params: defaultParams });
    }

    const urlDefault = await withUrlParamsAndUserId(API.URL_SET_VAT_CONFIG_DEFAULTS, { siteName, ...params });

    return axiosWithAuth.put<Cart>(urlDefault, null, { params: defaultParams });
};

export const apiSetCompanyInfo = async (segment: TeliaSegment, params: CartIdParam, body: CompanyInfoRequestBody) => {
    const siteName = getSiteName(segment);
    const url = await withUrlParamsAndUserId(API.URL_SET_COMPANY_INFO, { siteName, ...params });

    return axiosWithAuth.put<Cart>(url, body, { params: defaultParams });
};

export const apiChangeCardType = async (segment: TeliaSegment, params: CartIdParam, cardType: CardType) => {
    const siteName = getSiteName(segment);
    const url = await withUrlParamsAndUserId(API.URL_UPDATE_CARD_TYPE, { siteName, ...params });

    return axiosWithAuth.put<Cart>(url, null, { params: { ...defaultParams, entryType: cardType } });
};

const withUrlParamsAndUserId = async (url: string, params?: object): Promise<string> =>
    withParams(url, {
        ...params,
        userId: await getUserId(),
    });

export const getAllAccounts = (
    segment: CommonModel.Segment,
    subscriptionOptions: AccountSubscriptionOption = 'E_SIM',
) => {
    return axios
        .get<Account[]>(withParams(API.URL_ALL_SUBSCRIPTIONS, { segment }), {
            params: { subscriptionOptions },
        })
        .catch((error) => {
            const datadogLogs = datadogLogger();
            datadogLogs.error(error, { component: DDComponent.EshopCheckoutB2C });

            return { data: [] };
        });
};

export const apiGetBaseProduct = async (segment: TeliaSegment, productCode: string) => {
    const siteName = getSiteName(segment);
    const url: string = withParams(API.URL_PRODUCT, { siteName, productCode });

    return axiosForHybris.get<BaseProduct>(url, { params: { ...baseProductFilteringParams, byPath: true } });
};
